'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  avatar: string;
  units: number;
}

const testimonials: Testimonial[] = [
{
  quote: "I went from spending Sunday nights in spreadsheets to actually having a Sunday. Propmatic replaced three tools I was paying for separately.",
  name: "Marcus Johansson",
  role: "Independent landlord, 68 units — Denver, CO",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_130a11f32-1772230848591.png",
  units: 68
},
{
  quote: "Our investors used to get a PDF that took me 4 hours to compile. Now they get a live link that updates itself. That alone was worth the switch.",
  name: "Priya Nair",
  role: "Managing Partner, Nair Capital Group — Austin, TX",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9480b4c-1772230851925.png",
  units: 214
},
{
  quote: "The maintenance queue alone saves my team about 6 hours a week. Tickets used to fall through the cracks. Now everything has a timestamp.",
  name: "Derek Washington",
  role: "Operations Director, Apex PM Group — Atlanta, GA",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc086b6b-1772230851166.png",
  units: 340
},
{
  quote: "We tried three other platforms before Propmatic. This is the first one that doesn't feel like it was designed for a demo, not actual use.",
  name: "Sarah Kowalczyk",
  role: "Property Manager, Lakeside Residential — Chicago, IL",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e1b8880a-1772230848718.png",
  units: 127
},
{
  quote: "Late payments dropped 40% in the first 90 days. The automated reminder sequences work. I was skeptical — I'm not anymore.",
  name: "James Okafor",
  role: "Owner, Okafor Properties LLC — Houston, TX",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17d8f5ba1-1772230848951.png",
  units: 89
},
{
  quote: "Propmatic made it possible to add 40 units without adding headcount. The ROI was obvious within the first month.",
  name: "Rachel Kim",
  role: "CEO, Summit Property Group — Seattle, WA",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19b3c88ee-1772230852617.png",
  units: 156
},
{
  quote: "The lease expiration dashboard alone caught two renewals I would have missed. That's thousands of dollars in vacancy costs avoided.",
  name: "Thomas Bergström",
  role: "Syndicator, Nordic Capital RE — Minneapolis, MN",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15c95ff79-1772230848292.png",
  units: 203
},
{
  quote: "Finally, software that feels like it was built by people who actually manage properties. Every feature solves a real problem.",
  name: "Angela Morales",
  role: "Founder, Morales Property Management — Phoenix, AZ",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1be8cee72-1772066196649.png",
  units: 92
}];


function TestimonialCard({ t }: {t: Testimonial;}) {
  return (
    <div
      className="w-[400px] shrink-0 glass-panel rounded-2xl p-8 flex flex-col justify-between cursor-default transition-all duration-500 hover:border-[rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.08)]"
      style={{ height: 220 }}>
      
      <p className="text-[#9999A8] text-base font-light leading-relaxed line-clamp-3">
        &ldquo;{t.quote}&rdquo;
      </p>
      <div className="flex items-center gap-3 pt-5 border-t border-[rgba(255,255,255,0.04)] mt-4">
        <AppImage
          src={t.avatar}
          alt={`${t.name} — ${t.role}`}
          width={36}
          height={36}
          className="rounded-full ring-2 ring-[rgba(124,58,237,0.3)] object-cover w-9 h-9 shrink-0" />
        
        <div className="min-w-0">
          <div className="text-sm font-manrope font-semibold text-[#EEEEF0] truncate">{t.name}</div>
          <div className="text-[11px] font-mono text-[#5A5A6E] truncate">{t.role}</div>
        </div>
        <div className="ml-auto shrink-0 text-right">
          <div className="text-xs font-mono text-violet-light">{t.units}u</div>
        </div>
      </div>
    </div>);

}

export default function TestimonialsMarquee() {
  const row1 = [...testimonials.slice(0, 4), ...testimonials.slice(0, 4)];
  const row2 = [...testimonials.slice(4), ...testimonials.slice(4)];

  return (
    <section className="py-24 overflow-hidden relative">
      {/* Gradient masks — use --void so it adapts to theme */}
      <div
        className="absolute inset-y-0 left-0 w-32 z-20 pointer-events-none"
        style={{ background: 'linear-gradient(to right, var(--void), transparent)' }}
      />
      <div
        className="absolute inset-y-0 right-0 w-32 z-20 pointer-events-none"
        style={{ background: 'linear-gradient(to left, var(--void), transparent)' }}
      />

      <div className="max-w-7xl mx-auto px-6 mb-14">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(124,58,237,0.25)] mb-6">
          <span className="text-[10px] font-mono text-violet-light tracking-widest uppercase">Customer Stories</span>
        </div>
        <h2
          className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0] leading-tight"
          style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}>
          
          Trusted by landlords who stopped
          <span className="text-violet-light"> tolerating spreadsheets.</span>
        </h2>
      </div>

      {/* Row 1 — scrolls left */}
      <div className="w-full overflow-hidden mb-4">
        <div className="animate-marquee-left marquee-track flex gap-6 pl-6 py-2">
          {row1.map((t, i) =>
          <TestimonialCard key={i} t={t} />
          )}
        </div>
      </div>

      {/* Row 2 — scrolls right */}
      <div className="w-full overflow-hidden">
        <div className="animate-marquee-right marquee-track flex gap-6 pl-6 py-2">
          {row2.map((t, i) =>
          <TestimonialCard key={i} t={t} />
          )}
        </div>
      </div>
    </section>);

}